/**
 * CapabilitiesContent — full platform deep-dive, rendered inside the capabilities modal.
 *
 * Layout: two-column — sticky left nav + scrollable right content.
 * Collates ALL sector detail (challenge, stakeholders, workflow steps, capabilities,
 * AI reasoning, sample queries) from both the old AuthLayout right panel and the
 * previous CapabilitiesContent. No MVP numbers, no version tags.
 *
 * A `defaultSection` prop allows the tile click on the login right-panel to
 * pre-scroll directly to the relevant vertical section.
 */

import { useEffect, useRef, useState } from 'react';

// ─── Nav sections ─────────────────────────────────────────────────────────────

interface NavSection {
  id: string;
  label: string;
  icon: string;
}

const NAV_SECTIONS: NavSection[] = [
  { id: 'platform',      label: 'Platform Overview',         icon: '⚙️' },
  { id: 'loop',          label: 'The Intelligence Loop',     icon: '🔄' },
  { id: 'trust',         label: 'Trust & Compliance',        icon: '🔒' },
  { id: 'agrotech',      label: 'AgroTech Intelligence',     icon: '🌿' },
  { id: 'precision',     label: 'Precision Planning',        icon: '🎯' },
  { id: 'oilgas',        label: 'Oil & Gas Intelligence',    icon: '🛢️' },
  { id: 'infrastructure',label: 'Infrastructure & Construction', icon: '🏗️' },
  { id: 'linearassets',  label: 'Linear Assets Intelligence', icon: '⚡' },
  { id: 'geotech',       label: 'Environmental Geotech',     icon: '🌍' },
  { id: 'asknabhos',     label: 'Ask Nabhos',                icon: '💬' },
  { id: 'competitive',   label: 'Competitive Advantage',     icon: '🏆' },
];

// ─── Shared sub-components ────────────────────────────────────────────────────

function SectionAnchor({ id }: { id: string }) {
  return <div id={id} className="scroll-mt-4" />;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-1 pb-2 border-b-2 border-gray-200 dark:border-gray-700 first:mt-0">
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mt-5 mb-2">
      {children}
    </h3>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-2">
      {children}
    </p>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-4 border-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-r-md px-4 py-3 my-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">
      {children}
    </div>
  );
}

function Tagline({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-4 border-violet-400 bg-violet-50 dark:bg-violet-900/20 rounded-r-md px-4 py-2.5 my-2 text-sm italic text-gray-600 dark:text-gray-400">
      {children}
    </div>
  );
}

function AiBadge({ badge, text, prompt }: { badge: string; text: string; prompt?: string }) {
  return (
    <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 mt-4">
      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded mb-2 bg-green-700 dark:bg-green-800 text-white">
        {badge}
      </span>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{text}</p>
      {prompt && (
        <p
          className="mt-3 text-sm italic text-green-700 dark:text-green-400"
          style={{ fontFamily: "'Architects Daughter', cursive" }}
        >
          {prompt}
        </p>
      )}
    </div>
  );
}

function DataTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto my-3">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="bg-gray-100 dark:bg-gray-800 text-left px-3 py-2 border border-gray-200 dark:border-gray-700 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 1 ? 'bg-gray-50 dark:bg-gray-800/40' : 'bg-white dark:bg-gray-900/20'}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top text-gray-700 dark:text-gray-300"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StakeholderGrid({ stakeholders }: { stakeholders: { role: string; desc: string }[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
      {stakeholders.map((s) => (
        <div key={s.role} className="rounded-md p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-0.5">{s.role}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{s.desc}</p>
        </div>
      ))}
    </div>
  );
}

function WorkflowSteps({ steps }: { steps: string[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-3">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2">
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white mt-0.5"
            style={{ background: '#276749' }}
          >
            {i + 1}
          </span>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{step}</p>
        </div>
      ))}
    </div>
  );
}

function CapabilityList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 my-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="flex-shrink-0 text-green-500 dark:text-green-400 mt-0.5">✓</span>
          <span className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function WorkflowCard({ icon, name, steps, features }: {
  icon: string; name: string; steps: string[]; features: string[];
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{icon}</span>
        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">{name}</p>
      </div>
      <WorkflowSteps steps={steps} />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {features.map((f, i) => (
          <span
            key={i}
            className="text-xs px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface CapabilitiesContentProps {
  defaultSection?: string;
}

export default function CapabilitiesContent({ defaultSection }: CapabilitiesContentProps) {
  const [activeNav, setActiveNav] = useState(defaultSection ?? 'platform');
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to the default section when opened via a vertical tile click
  useEffect(() => {
    if (defaultSection) {
      setActiveNav(defaultSection);
      const el = document.getElementById(defaultSection);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [defaultSection]);

  // Update active nav on scroll — use getBoundingClientRect so nesting depth doesn't matter
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      for (const s of [...NAV_SECTIONS].reverse()) {
        const el = container.querySelector(`#${s.id}`);
        if (el) {
          const elTop = el.getBoundingClientRect().top - containerTop;
          if (elTop <= 80) {
            setActiveNav(s.id);
            break;
          }
        }
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setActiveNav(id);
    const container = contentRef.current;
    const el = container?.querySelector(`#${id}`);
    if (el && container) {
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top - containerTop;
      container.scrollBy({ top: elTop - 16, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex h-full bg-white dark:bg-gray-900">

      {/* ── Left nav ──────────────────────────────────────────────── */}
      <nav className="w-48 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-y-auto py-4">
        {NAV_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(s.id)}
            className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
              activeNav === s.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-semibold border-r-2 border-blue-500'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/40'
            }`}
          >
            <span className="text-sm flex-shrink-0">{s.icon}</span>
            <span className="leading-snug">{s.label}</span>
          </button>
        ))}
      </nav>

      {/* ── Scrollable content ────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="px-7 py-6 max-w-3xl">

          {/* ══════════════════════════════════════════════════════
              PLATFORM OVERVIEW
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="platform" />
          <SectionTitle>Platform Overview</SectionTitle>
          <Tagline>
            The bridge between a raw satellite image and a boardroom decision.
          </Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            <strong>Nabhos</strong> (from Sanskrit <em>Nabhas</em> = Sky) is a professional-grade, multi-tenant agentic intelligence platform that bridges free satellite imagery and precision drone telemetry with actionable business decisions — across every industry that operates, monitors, or governs land, assets, or corridors from the air.
          </p>
          <Callout>
            <strong>The core promise:</strong> Stop staring at thousands of photos. Talk to your data.
            Nabhos replaces expensive, manual, reactive field operations with a continuous AI-driven intelligence loop —
            from first satellite pixel to final remediation record, every step traceable, every decision auditable.
          </Callout>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            One platform. Five industry verticals. One conversational AI that knows your entire operational history. The same nine-step intelligence loop runs across all verticals — only the vocabulary, indices, thresholds, sensor payloads, and response types differ, and those are controlled by configuration, not code.
          </p>

          <SubTitle>Who Uses It</SubTitle>
          <DataTable
            headers={['Role', 'What They Do']}
            rows={[
              [<strong>Operations & Field Manager</strong>, 'Monitors satellite alerts, initiates drone missions, reviews AI health insights, confirms remediation actions'],
              [<strong>Drone Technician</strong>, 'Uploads RGB, Thermal, Multispectral, LiDAR, and OGI telemetry; validates hardware against the Approved Asset List'],
              [<strong>Organisation Admin</strong>, 'Configures thresholds, AI model providers, chemical safety limits, hierarchy nodes, and compliance settings'],
              [<strong>Platform Administrator</strong>, 'Provisions organisations, activates industry modules, monitors platform-wide usage and analytics'],
            ]}
          />

          <SubTitle>Deployment Model</SubTitle>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Industry verticals are activated per-organisation through the Module Marketplace — instantly, without code deployment or session restart. Activating a module provisions the vertical's spatial layers, RBAC roles, AI reasoning templates, and navigation sections automatically.
          </p>
          <DataTable
            headers={['Industry Vertical', 'Activated Via']}
            rows={[
              ['Platform Foundation (security, audit, ingestion, notifications)', 'Included in all deployments'],
              ['AgroTech Estate Intelligence', 'Module Marketplace — per organisation'],
              ['Precision Planning (variable-rate, yield forecasting, suitability)', 'Add-on to AgroTech'],
              ['Oil & Gas Intelligence', 'Module Marketplace — per organisation'],
              ['Infrastructure & Construction', 'Module Marketplace — per organisation'],
              ['Environmental Geotech', 'Module Marketplace — per organisation'],
            ]}
          />

          {/* ══════════════════════════════════════════════════════
              THE INTELLIGENCE LOOP
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="loop" />
          <SectionTitle>The 9-Step Intelligence Loop</SectionTitle>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            Every Nabhos vertical runs on the same nine-step loop. The loop is structurally identical across agriculture, energy, infrastructure, and environmental monitoring — only the labels, satellite indices, anomaly thresholds, drone payload recommendations, and response action types differ per vertical.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 my-4">
            {[
              { n: '1', title: 'Boundary Definition',       actor: 'Ops Manager',     desc: 'Draw or upload working area; attach vertical-specific metadata (crop type, pipeline ID, regulatory designation, etc.)' },
              { n: '2', title: 'Satellite Survey',          actor: 'Automated',       desc: 'Sentinel-2 (10 m, 5-day) and Landsat 8/9 (30 m, 16-day) imagery fetched, cloud-filtered, indexed, and cached' },
              { n: '3', title: 'AI Anomaly Analysis',       actor: 'AI Engine',       desc: 'Delta engine computes index changes, applies vertical thresholds, generates probable-cause narrative with confidence score' },
              { n: '4', title: 'Human Verification Gate 1', actor: 'Ops Manager',     desc: 'Confirm → dispatch drone / Reject → reason code / Escalate → management / Defer → reminder. Full audit record.' },
              { n: '5', title: 'Drone Survey Planning',     actor: 'AI + Ops Manager',desc: 'Auto-generated flight plan: AI-recommended payload, altitude, overlap, weather window, waypoint density, safety checks' },
              { n: '6', title: 'Drone Execution + AI',      actor: 'Technician + AI', desc: 'Upload telemetry; AI analyses drone data in context of the originating satellite anomaly and field metadata' },
              { n: '7', title: 'Human Verification Gate 2', actor: 'Ops Manager',     desc: 'Confirm diagnosis / Override with own assessment (reason code required) / Request re-survey. Audit record.' },
              { n: '8', title: 'Response / Remediation',    actor: 'Ops Manager + Tech', desc: 'Targeted action dispatched (spray, pressure test, enforcement order). Verification re-survey auto-scheduled.' },
              { n: '9', title: 'Ask Nabhos',                actor: 'Ops Manager',     desc: 'Natural language interrogation across all steps, all verticals, all history. Voice input. Conversation PDF export.' },
            ].map((s) => (
              <div key={s.n} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: '#276749' }}
                  >
                    {s.n}
                  </span>
                  <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{s.title}</p>
                </div>
                <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mb-1">{s.actor}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* ══════════════════════════════════════════════════════
              TRUST & COMPLIANCE
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="trust" />
          <SectionTitle>Trust & Compliance</SectionTitle>

          <SubTitle>Enterprise Security & Multi-Tenancy</SubTitle>
          <CapabilityList items={[
            'Row-Level Security (RLS) enforced at the database layer — every query automatically filtered by tenant_id; manual UUID bypass returns 403 Forbidden',
            'JWT authentication + Multi-Factor Authentication (TOTP, SMS, email) — configurable per organisation',
            'Single Sign-On (SSO) support; cross-tenant superadmin context switching without logout',
            'Data sovereignty — configurable geographic data residency; GDPR and India DPDP compliant',
            'Permission evaluation is dynamic at runtime from a cached database registry — not hardcoded enum checks. Role changes propagate across all active sessions within 60 seconds.',
          ]} />

          <SubTitle>The Digital Seal of Integrity (SHA-256)</SubTitle>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Every piece of data in Nabhos carries a cryptographic hash — computed on ingestion and stored immutably. The chain is unbroken from satellite tile to final remediation record:
          </p>
          <CapabilityList items={[
            'Every uploaded file (RGB, Thermal, LiDAR, OGI video) — block-level SHA-256 checksum during chunked upload',
            'Every satellite tile, every anomaly delta calculation, every remediation plan — sealed with SHA-256 on creation',
            'End-to-end chain: satellite anomaly ID → drone analysis record → remediation plan, all cryptographically linked',
            'One-click Certified Mission Report (PDF) displaying the SHA-256 hash of every source used',
            'Oil & Gas critical anomalies: extended retention 5 years. Environmental regulatory anomalies: 7 years.',
            'All evidence packages suitable for submission to DPR/NUPRC, EPA, Wetlands authorities, insurance, and legal proceedings',
          ]} />

          <SubTitle>AI Model Flexibility & Factuality</SubTitle>
          <CapabilityList items={[
            'Active providers: Google Gemini (default), Cloudflare Workers AI, Groq, OpenRouter — configurable per tenant',
            'OpenAI and Anthropic staged providers available for activation (see platform documentation)',
            'Per-module model selection — lightweight model for notifications; high-reasoning model for satellite temporal analysis',
            'Bring Your Own Key (BYOK) via Google Secret Manager',
            'Narratives generated in the tenant\'s configured primary language (English, Hindi, Spanish, French, etc.)',
            'Grounded-only factuality guardrails — AI never asserts a finding not supported by underlying telemetry',
            'Prompt injection filtering; mandatory source citation in every AI-generated report',
          ]} />

          <SubTitle>Resilient Data Ingestion</SubTitle>
          <CapabilityList items={[
            'Chunked, resumable upload — 5 MB chunks; resumes from last checkpoint after any network interruption; supports files exceeding 10 GB and multi-hour sessions',
            'Accepted formats: RGB (TIFF/JPG), Multispectral (TIFF + band metadata), Thermal (TIFF/RJPEG), LiDAR point cloud (LAS/LAZ), OGI video (RJPEG/SER/MP4), Flight logs (CSV/KML/DJI .DAT), Boundaries (GeoJSON, KML/KMZ, ESRI Shapefile)',
            'Approved Asset List validation — pilot ID and drone/sensor serial numbers checked before every mission initialisation',
          ]} />

          {/* ══════════════════════════════════════════════════════
              AGROTECH
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="agrotech" />
          <SectionTitle>AgroTech Intelligence</SectionTitle>
          <Tagline>"Stop staring at thousands of photos. Talk to your data."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Continuous satellite monitoring, AI anomaly detection, drone-confirmed diagnosis, and precision remediation — from a single canopy stress signal to a targeted sprayer drone mission, all in one closed loop.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Monitoring thousands of acres through manual ground patrols causes delayed anomaly detection, reactive (rather than preventive) intervention, and an inability to correlate crop stress with environmental drivers across time. Blanket-rate chemical application wastes up to 30% of inputs.
          </p>

          <Label>Relevant Stakeholders</Label>
          <StakeholderGrid stakeholders={[
            { role: 'Operations & Field Manager', desc: 'Monitors satellite alerts, initiates drone missions, reviews AI health insights, and confirms remediation actions via the HITL framework.' },
            { role: 'Drone Technician', desc: 'Uploads RGB, Thermal, Multispectral, and LiDAR telemetry; validates hardware against the Approved Asset List.' },
            { role: 'Organisation Admin', desc: 'Configures crop benchmarks, chemical safety thresholds, hierarchy nodes, and AI model providers.' },
          ]} />

          <Label>Estate Intelligence Workflow</Label>
          <WorkflowSteps steps={[
            'Define field boundaries & crop hierarchy on interactive map (crop type, variety, planting date, irrigation type)',
            'Free satellite imagery (Sentinel-2/Landsat) ingested automatically on every overpass — NDVI, NDRE, EVI, NDWI, LST indices computed',
            'AI detects anomaly (NDVI drop >15%, bare soil >5%, NDRE increase >20%); generates probable-cause narrative with ranked confidence scores',
            'Operations Manager reviews anomaly and verifies: confirm drone survey / reject with reason / escalate / defer',
            'AI auto-generates drone survey plan: recommended sensor payload, altitude, overlap, weather window, waypoint cluster over anomalous zone',
            'Drone Technician executes flight; uploads telemetry; AI diagnoses root cause from drone data vs. satellite finding',
            'Operations Manager reviews drone AI diagnosis: confirm / override with agronomist expertise / request re-survey',
            'AI generates targeted remediation mission (spray, fertiliser, marker); compliance guardrails hard-enforced; dispatched to Technician',
            'Verification re-survey auto-scheduled after treatment; treatment efficacy score generated from NDVI recovery',
          ]} />

          <Label>Satellite Indices — AgroTech</Label>
          <DataTable
            headers={['Index', 'What It Detects', 'Threshold']}
            rows={[
              [<strong>NDVI</strong>, 'Overall crop vigour and chlorophyll content', 'Drop >15% in 14 days → alert'],
              [<strong>NDRE</strong>, 'Early water stress; canopy nitrogen status', 'Increase >20% → alert'],
              [<strong>EVI</strong>, 'Vigour in high-biomass areas (mature tea, oil palm)', 'Trend analysis'],
              [<strong>NDWI</strong>, 'Soil moisture; waterlogging detection', 'Flooding + DEM low-point correlation'],
              [<strong>LST (Thermal)</strong>, 'Surface temperature; heat stress; drainage hotspots', 'Deviation from seasonal baseline'],
            ]}
          />

          <Label>AI Drone Survey Payload Recommendations</Label>
          <DataTable
            headers={['Anomaly Type', 'AI Payload Recommendation', 'Altitude']}
            rows={[
              ['Vigour / chlorophyll drop', 'Multispectral — MicaSense RedEdge-P (Red Edge + NIR + RGB)', '90–120 m'],
              ['Water stress (NDRE/NDWI)', 'Multispectral + Thermal — FLIR Vue Pro R', '80–100 m'],
              ['Waterlogging / drainage failure', 'Thermal + LiDAR — FLIR Boson 320 + DJI Zenmuse L2', '60–80 m'],
              ['Bare soil / erosion', 'High-res RGB — Sony RX1R II / Zenmuse P1 + Multispectral', '80–100 m'],
              ['Pest / infestation indicator', 'High-res Multispectral — MicaSense Altum-PT (low altitude)', '60–80 m'],
            ]}
          />

          <Label>Solution Capabilities</Label>
          <CapabilityList items={[
            'Continuous free satellite monitoring (Sentinel-2, Landsat) across any estate size',
            'Conversational NLQ — "Ask Nabhos" temporal trend queries across missions and seasons',
            'Agentic AI root-cause diagnosis grounded in real-time weather and environmental data',
            'Precision spray drone remediation — exact sub-zone targeting, maximum dosage hard-enforced',
            'No-spray buffers around water bodies auto-applied; cannot be overridden by field managers',
            'Treatment Efficacy scoring — pre/post NDVI recovery measurement after every intervention',
            'Certified SHA-256 mission reports for regulators and insurers',
          ]} />

          <AiBadge
            badge="Agentic AI Reasoning"
            text="Correlates multi-spectral indices (NDVI, NDRE, NDWI) against multi-year seasonal baselines and live environmental adapters to auto-diagnose stress vectors, predict yield deltas, and generate ranked remediation recommendations — all with a cryptographically verifiable audit trail."
            prompt='Ask Nabhos: "How has crop vigour on the East Block changed over the last three months, and what is driving the decline?"'
          />

          {/* ══════════════════════════════════════════════════════
              PRECISION PLANNING
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="precision" />
          <div className="flex items-center gap-2 mt-8 mb-1 pb-2 border-b-2 border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Precision Planning
            </h2>
            <span className="px-2 py-0.5 rounded text-xs font-bold bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300">
              Advanced AgroTech Add-on
            </span>
          </div>
          <Tagline>"Cut chemical waste by 30% with AI-generated prescription maps your sprayer reads directly."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            An advanced agronomic add-on module to AgroTech estate intelligence that delivers variable-rate prescriptions, predictive yield forecasting, crop suitability indexing, and strategic land expansion ROI modelling.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Blanket-rate chemical application wastes up to 30% of inputs, while crop transition decisions based on intuition rather than multi-year environmental data lead to costly failed investments.
          </p>

          <Label>Precision Planning Workflow</Label>
          <WorkflowSteps steps={[
            'AI forecasts infestation hotspots from seasonal history and live climate data',
            'Optimised drone flight path generated — dense waypoints over high-risk zones, lighter over low-risk areas',
            'Multi-index analysis (NDVI, Thermal, Soil) drives zonal dosage calculation per sub-zone',
            'Variable-rate prescription map generated with regulatory guardrails and chemical safety limits',
            'Manager reviews, runs What-If simulations (different rates, different chemicals), confirms allocation',
            'Machine-ready ISOXML / Shapefile export — directly compatible with tractor terminals and precision sprayers',
          ]} />

          <Label>Solution Capabilities</Label>
          <CapabilityList items={[
            'AI-driven infestation probability heatmaps from historical seasonal patterns and live weather',
            'Variable-rate prescription maps with per-zone chemical dosages and regulatory safety guardrails',
            'Maximum allowable dosage hard-enforced — plan blocked, not just warned, if exceeded',
            'ISOXML (ISO 11783) and ESRI Shapefile exports for direct tractor terminal integration',
            'Predictive yield forecasting with confidence intervals and What-If scenario analysis (rainfall, temperature variables)',
            'High-value crop suitability indexing (Kiwi, Avocado, Coffee, etc.) — score 0–100% per plot',
            'Strategic land expansion suitability dossiers for acquisition decisions with DEM-based development effort estimates',
            'Multi-year NPV, CAPEX profiling, yield ramp-up, break-even timeline, one-click board-ready PDF investment memo',
            'Chemical savings analysis: real-time delta between blanket-rate baseline and variable-rate prescription',
          ]} />

          <AiBadge
            badge="Agentic AI Reasoning"
            text="Synthesises NDVI vigour, thermal water-stress, and grounded soil sensor data to compute precise per-zone chemical dosages; flags regulatory exceedances; and generates ranked crop suitability scores correlated against multi-year climate and topographic profiles."
            prompt='Ask Nabhos: "Which zones on Estate B need targeted spot-spray this week, and what is the projected chemical saving vs. last season?"'
          />

          {/* ══════════════════════════════════════════════════════
              OIL & GAS
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="oilgas" />
          <SectionTitle>Oil & Gas Intelligence</SectionTitle>
          <Tagline>"Detect a hydrocarbon leak through vegetation die-off — weeks before it surfaces."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Continuous satellite surveillance of pipeline corridors for leak indicators, encroachment, and thermal anomalies — with OGI drone dispatch, HSSE sign-off gate, and direct SAP/Oracle work order generation.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Undetected hydrocarbon leaks carry catastrophic safety, environmental, and legal risks. Manual right-of-way patrols cannot continuously monitor thousands of kilometres of pipeline corridor for encroachment, vegetation die-off, or invisible gas plumes.
          </p>

          <Label>Relevant Stakeholders</Label>
          <StakeholderGrid stakeholders={[
            { role: 'Pipeline Integrity Manager', desc: 'Oversees satellite corridor scans, reviews AI threat alerts, and approves drone dispatch for confirmed anomalies.' },
            { role: 'Drone / OGI Technician', desc: 'Operates Optical Gas Imaging and thermal sensors to capture and upload gas plume telemetry.' },
            { role: 'HSSE Safety Officer', desc: 'Reviews AI OGI/thermal diagnosis; records safety decision before any incident response is triggered.' },
            { role: 'Upstream Exploration Lead', desc: 'Commissions magnetometry and seismic survey drone missions for prospective field analysis.' },
            { role: 'Maintenance Lead (ERP User)', desc: 'Receives AI-verified repair work orders automatically triggered in SAP or Oracle.' },
          ]} />

          <WorkflowCard
            icon="🔧"
            name="Pipeline & Corridor Monitoring"
            steps={[
              'Draw pipeline RoW boundary; system auto-buffers corridor polygon (default 50 m bilateral)',
              'Free Sentinel-2 imagery ingested on every overpass — no subscription required',
              'AI computes NDVI die-off and NDBI encroachment delta vs. 90-day baseline',
              'Ranked anomaly queue generated; Pipeline Integrity Manager reviews and confirms',
              'OGI / thermal drone dispatched to confirmed zone; FLIR GF320 at 30 m AGL (wind <3 m/s required)',
              'SHA-256 sealed finding record written; SAP/Oracle work order triggered',
            ]}
            features={['RoW polygon & line buffer mapping', 'Multi-temporal NDVI/thermal layer comparison', 'Vegetation die-off, encroachment & gas plume anomaly queue']}
          />

          <Label>Satellite Indices — Oil & Gas</Label>
          <DataTable
            headers={['Index', 'Leak / Threat Indicator', 'Critical Threshold']}
            rows={[
              [<strong>NDVI die-off</strong>, 'Vegetation kill-off along RoW — earliest detectable leak signature', 'Drop >25% in corridor buffer'],
              [<strong>LST (Thermal)</strong>, 'Temperature anomaly — product seepage heating soil', '>5 °C above 30-day rolling baseline'],
              [<strong>NDBI</strong>, 'Third-party encroachment; new construction on right-of-way', 'New signature vs. baseline'],
              [<strong>NDWI</strong>, 'Moisture changes from produced water leaks', 'Boundary shift >1%'],
            ]}
          />
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
            <strong>Combined critical-leak logic:</strong> NDVI drop &gt;25% in corridor buffer <em>and</em> LST anomaly in the same cell → automatic Critical severity, immediate push notification (not 24-hour batch). Seismic event correlation: events within 50 km trigger automatic re-query of all corridors in that radius within 24 hours.
          </p>

          <WorkflowCard
            icon="🛢️"
            name="Upstream Exploration"
            steps={[
              'Register aeromagnetic, hyperspectral, seismic 2D/3D, or gravity survey mission',
              'Spectral anomaly overlays processed; subsurface signatures ranked by AI',
              'OGI sensor telemetry ingested; AI bounds plume polygon and estimates CH₄ emission volume',
              'Facility thermal baseline established; flare intensity deviation flagged',
              'HSSE Officer confirms finding and severity classification — Step 7 gate',
              'Outbound work order pushed to SAP / Oracle / Maximo with DLQ retry guarantee',
            ]}
            features={['Upstream Exploration — magnetometry / hyperspectral overlays', 'Plume & Leak Analyser — OGI telemetry & plume bounding', 'Facility Safety & Flaring — thermal baselines & flame intensity', 'ERP Integration Hub — SAP/Oracle/Maximo work order status & DLQ']}
          />

          <WorkflowCard
            icon="💼"
            name="Value Realisation & ROI"
            steps={[
              'Closed incidents aggregated; product loss volume (bbl) and plume rate (kg/hr) extracted',
              'Intercepted product value calculated from repair count × crude price ($/bbl)',
              'Regulatory fine avoidance estimated per notified incident against jurisdiction thresholds',
              'CH₄ mass × IPCC AR6 GWP-100 factor converts plume data to CO₂-equivalent prevented',
              'SHA-256 sealed evidence packs compiled for DPR/NUPRC, EPA, or internal audit submission',
              'Executive ROI multiple: total intercepted value ÷ estimated survey cost per mission',
            ]}
            features={['Product loss quantification (bbl → USD)', 'Regulatory fine avoidance', 'CO₂-equivalent prevented reporting (IPCC AR6)', 'SHA-256 sealed regulatory evidence packs (DPR/NUPRC/EPA)', 'Cumulative ROI multiple dashboard']}
          />

          <AiBadge
            badge="Agentic AI Reasoning"
            text="Cross-references satellite vegetation die-off patterns, thermal anomalies, and OGI plume signatures to automatically isolate pipeline leak locations, estimate methane volume, and generate legally defensible incident reports with full cryptographic provenance."
            prompt='Ask Nabhos: "Show me any vegetation die-off or thermal anomalies along the Sector 7 corridor detected in the last 30 days."'
          />

          {/* ══════════════════════════════════════════════════════
              INFRASTRUCTURE
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="infrastructure" />
          <SectionTitle>Infrastructure & Construction</SectionTitle>
          <Tagline>"Know about the unauthorised earthwork at Km 47.2 before your field team does."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Satellite-first construction monitoring and linear asset surveillance — with LiDAR-grade legal evidence and volumetric earthwork calculations.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Manual inspections of road networks, power lines, and bridges miss early-stage structural wear until it becomes a costly failure. Construction site volumetrics and progress reporting rely on slow manual surveys that cannot keep pace with project timelines. Unauthorised construction proceeds undetected between inspection cycles.
          </p>

          <Label>Relevant Stakeholders</Label>
          <StakeholderGrid stakeholders={[
            { role: 'Civil Infrastructure Engineer', desc: 'Reviews satellite and drone-derived wear analysis for roads, bridges, and power line corridors.' },
            { role: 'Project Construction Manager', desc: 'Tracks cut/fill volumes, site clearing progress, and material stockpile quantities via 3D drone models.' },
            { role: 'Asset Network Operator', desc: 'Monitors power line and railway corridor encroachment and vegetation management compliance.' },
          ]} />

          <Label>Infrastructure Monitoring Workflow</Label>
          <WorkflowSteps steps={[
            'Define linear corridor or construction site boundary — roads, rail, bridges, slopes, power lines',
            'Satellite screening for construction progress, new structures, encroachment, or surface anomalies (NDBI, DEM differencing)',
            'AI flags high-priority zones; Operations Manager confirms targeted drone survey dispatch',
            'LiDAR point cloud and optical drone data ingested and processed',
            'AI calculates cut/fill volumes, detects surface wear, generates change report vs. previous epoch',
            'Operations Manager reviews findings; certified SHA-256 report generated for project records or regulatory submission',
          ]} />

          <Label>Satellite Indices & Detection Methods</Label>
          <DataTable
            headers={['Method', 'Infrastructure Use', 'Threshold']}
            rows={[
              [<strong>NDBI (Built-up Index)</strong>, 'New construction signature; unauthorized development', 'New signature not on approved plan → High/Critical'],
              [<strong>Urban Index (UI)</strong>, 'Built-up classification; land use change', 'Change vs. baseline'],
              [<strong>DEM differencing</strong>, 'Earthwork volume calculation; slope instability', 'Volume change >10%'],
              [<strong>NDWI / MNDWI</strong>, 'Water body changes; drainage disruption; flood risk', 'Boundary shift >1%'],
              [<strong>Terrain slope {'>'}25°</strong>, 'Landslide risk — auto-flags for LiDAR survey', 'Auto-flag regardless of spectral anomaly'],
            ]}
          />

          <Label>Solution Capabilities</Label>
          <CapabilityList items={[
            'Satellite-based construction progress tracking and unauthorised structure detection',
            'LiDAR point-cloud cut/fill volumetric calculations for earthworks management (≥50 pts/m²)',
            'Legal-grade drone evidence: high-res RGB at 2–3 cm/px GSD — photogrammetric quality for regulatory submission',
            'Early-stage structural wear detection on roadways, bridges, and power lines',
            'Power line and railway corridor vegetation encroachment monitoring with days-to-contact projection',
            'Railway track obstruction → Critical alert with <5 minute push notification',
            'AI 3D surface change detection between survey epochs',
            'Certified SHA-256 mission reports for regulatory submission and insurance claims',
          ]} />

          <AiBadge
            badge="AI Capability"
            text="Point-cloud change detection compares LiDAR epochs to automatically calculate earth-moving volumes, identify surface wear progression, and flag structural anomalies — with every finding cryptographically sealed and exportable for regulatory or insurance audit."
            prompt='Ask Nabhos: "How much earth has been moved on Site C this month and is construction on schedule?"'
          />

          {/* ══════════════════════════════════════════════════════
              LINEAR ASSETS
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="linearassets" />
          <SectionTitle>Linear Assets Intelligence (Power Lines & Railways)</SectionTitle>
          <Tagline>"Predict vegetation contact with power lines and detect railway track obstructions within 6 hours."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Continuous right-of-way corridor monitoring across power grid feeders, high-voltage transmission lines, and railway track networks with predictive clearance modeling and rapid obstruction triage.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Unchecked vegetation growth causes power line flashovers and catastrophic wildfire ignitions. Railway corridors face rockfalls, track buckling, and fallen trees that jeopardize safety. Manual foot patrols along thousands of kilometers of remote right-of-way cannot deliver the sub-6-hour SLA needed for rapid hazard response.
          </p>

          <Label>Relevant Stakeholders</Label>
          <StakeholderGrid stakeholders={[
            { role: 'Asset Integrity Engineer', desc: 'Monitors power-line clearance buffers, verifies vegetation encroachment rates, and approves tree-trimming work orders.' },
            { role: 'Railway Safety Officer', desc: 'Triages rapid track obstruction alerts (<6 h SLA), dispatches emergency maintenance, and oversees track safety.' },
            { role: 'Drone Patrol Pilot', desc: 'Executes corridor survey flights using LiDAR and high-res RGB sensors to capture sub-5cm canopy-to-wire distances.' },
            { role: 'Operations & Maintenance Lead', desc: 'Coordinates field contractor clearing crews and tracks post-clearance rescan efficacy.' },
          ]} />

          <Label>Linear Assets 8-Step Workflow</Label>
          <WorkflowSteps steps={[
            'Define power line / railway corridor centreline with configurable lateral buffer zones (e.g. 10m / 50m)',
            'Automated Sentinel-2 NDVI & thermal temporal monitoring on every orbital overpass',
            'AI calculates monthly growth rates (m/month) and projects days-to-contact per km segment',
            'Track obstruction rapid triage (<6 h SLA from satellite pass) with direct alert dispatch',
            'Automated corridor drone patrol mission planning with altitude and terrain-following configs',
            'LiDAR point-cloud and optical imagery upload with automated wire-to-canopy proximity calculation',
            'Asset integrity engineer sign-off and digital seal attestation on evidence packages',
            'Clearance response work order generation, contractor dispatch, and 14-day post-clearance rescan scheduling',
          ]} />

          <Label>Satellite & Drone Diagnostics</Label>
          <DataTable
            headers={['Diagnostic', 'Operational Indicator', 'Action Trigger']}
            rows={[
              [<strong>NDVI Encroachment Delta</strong>, 'Vegetation canopy spreading toward centreline', 'Projected contact <60 days → High Priority Trim'],
              [<strong>Track Obstruction Detection</strong>, 'Foreign object, rockfall, or thermal track buckling signature', 'Detected on track buffer → Immediate <6h SLA Alert'],
              [<strong>LiDAR Point-Cloud Distance</strong>, 'Sub-5cm measurement between wire and nearest branch', 'Wire clearance < buffer threshold → Immediate clearance'],
              [<strong>Thermal LST Hotspots</strong>, 'Transformer/line thermal overload or severe dry-brush fire hazard', 'Temperature >5°C above baseline → Thermal inspection'],
            ]}
          />

          <Label>Solution Capabilities</Label>
          <CapabilityList items={[
            'Predictive vegetation growth rate projection (m/month) with days-to-contact forecasting per km marker',
            'Rapid railway track obstruction alerts with sub-6-hour SLA latency from satellite pass',
            'Corridor segmentation and compliance reporting by kilometre marker',
            'Automated corridor drone survey flight path generation with multi-sensor payload selection',
            'Integration with tree trimming contractor work orders and audit logs',
            'Digital sealing of corridor inspection findings for regulatory utility commissions',
            'Post-clearance efficacy tracking comparing pre-trim vs. post-trim buffer clearance',
          ]} />

          <AiBadge
            badge="Agentic AI Reasoning"
            text="Extrapolates seasonal vegetation growth rates from historical multi-spectral imagery to accurately predict the date a tree canopy will breach legal power line clearances, enabling proactive trimming before flashovers occur."
            prompt='Ask Nabhos: "Which power line segments have less than 60 days to vegetation contact on the Sector 7 corridor?"'
          />

          {/* ══════════════════════════════════════════════════════
              ENVIRONMENTAL GEOTECH
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="geotech" />
          <SectionTitle>Environmental Geotech</SectionTitle>
          <Tagline>"Prove deforestation happened — with timestamped, tamper-proof satellite evidence."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
            Protected zone monitoring, carbon credit verification, geohazard risk assessment, and enforcement action workflows — with legally defensible evidence chains for regulatory submission.
          </p>

          <Label>Core Business Challenge</Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Governments and conservation bodies struggle to detect illegal deforestation, wetland encroachment, and mining activity in real time across vast protected zones. Carbon credit schemes lack defensible, tamper-proof biomass audit trails. Slope instability and flood risk along sensitive corridors goes undetected until catastrophic failure.
          </p>

          <Label>Relevant Stakeholders</Label>
          <StakeholderGrid stakeholders={[
            { role: 'Environmental Compliance Officer', desc: 'Monitors satellite anomaly alerts for deforestation, illegal clearing, and wetland encroachment; triggers enforcement actions with attached evidence packages.' },
            { role: 'Geotechnical Risk Specialist', desc: 'Tracks slope instability events and flood risk zones from SAR coherence and DEM analysis; dispatches civil engineering inspections and early-warning alerts.' },
            { role: 'Conservation Authority / Regulator', desc: 'Reviews canopy drone findings, verifies carbon credit biomass volumes, and receives SHA-256 sealed enforcement dossiers for legal proceedings.' },
            { role: 'Drone Technician', desc: 'Operates fixed-wing RGB + multispectral platforms over forest and wetland zones; uploads calibrated imagery and telemetry for AI canopy analysis.' },
          ]} />

          <Label>Environmental Geotech Workflow</Label>
          <WorkflowSteps steps={[
            'Define protected zone boundaries — Ramsar wetlands, national parks, forest reserves, carbon parcels, geohazard zones',
            'Sentinel-2 imagery fetched automatically; NDVI, NDWI, NBR, and LST indices computed per overpass',
            'AI detects canopy loss, wetland drainage, burn extent, or slope instability; ranked anomaly queue generated',
            'Environmental Officer reviews alert: confirm drone survey, notify authority, or defer',
            'Fixed-wing RGB + multispectral canopy survey planned; AI derives flight parameters for wide-area coverage',
            'Drone data uploaded; AI quantifies canopy loss (ha), burn area, wetland loss, and above-ground biomass (t/ha)',
            'Environmental Officer confirms severity and authorises enforcement — HITL gate 2',
            'Enforcement action dispatched: cease-and-desist, authority notification, remediation order, or legal referral',
          ]} />

          <Label>Satellite Indices — Environmental</Label>
          <DataTable
            headers={['Index', 'Environmental Threat', 'Threshold']}
            rows={[
              [<strong>NDVI / EVI</strong>, 'Canopy cover loss; deforestation detection', 'Forest cover loss >2% → Critical'],
              [<strong>NDWI / MNDWI</strong>, 'Wetland boundary extent; water body changes', 'Boundary shift >1%'],
              [<strong>NBR / BAI</strong>, 'Fire / burn severity; active fire detection', 'Burn index spike'],
              [<strong>NDBI</strong>, 'New road / illegal mining activity detection', 'New bare soil >1 ha + new access track'],
            ]}
          />

          <Label>Solution Capabilities</Label>
          <CapabilityList items={[
            'Multi-spectral zone monitoring: NDVI canopy, NDWI wetland moisture, NBR burn index, LST thermal anomaly',
            'AI canopy loss quantification: deforestation ha, burn area, wetland loss, above-ground biomass (t/ha)',
            'Carbon credit parcel verification — canopy cover %, CO₂ sequestration tracking, "at risk" flagging',
            'Evidence chain suitable for Verra / Gold Standard registry submission',
            'Geohazard risk — landslide, flood, and slope stability monitoring from SAR coherence + DEM analysis',
            'Enforcement action workflow: cease-and-desist orders, authority notification, remediation orders, legal referral',
            'AI narratives include enforcement authority contact and reference regulation configured per zone',
            'SHA-256 sealed enforcement dossiers for regulatory submission and legal proceedings',
            'All environmental regulatory anomaly records retained 7 years automatically',
          ]} />

          <AiBadge
            badge="Agentic AI Reasoning"
            text="Correlates multi-temporal NDVI, NDWI, and NBR satellite indices against zone baselines to automatically detect illegal clearing and wetland encroachment; quantifies canopy loss from fixed-wing multispectral imagery; and generates cryptographically sealed enforcement evidence packages — all with a full human-in-the-loop audit chain."
            prompt='Ask Nabhos: "Which protected zones have had significant canopy loss in the last 30 days, and what enforcement actions are outstanding?"'
          />

          {/* ══════════════════════════════════════════════════════
              ASK NABHOS
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="asknabhos" />
          <SectionTitle>Ask Nabhos — Conversational Intelligence</SectionTitle>
          <Tagline>"The bridge between a raw video feed and a boardroom decision."</Tagline>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            One universal natural language query interface — full context across all verticals, all 9 steps, and all historical data. No knowledge of satellite indices, database schemas, or query syntax required.
          </p>

          <Label>What You Can Ask</Label>
          <div className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-3">
            {[
              { tag: 'AgroTech', cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300', q: '"How has crop vigour changed on the East Block since the irrigation system was installed?"' },
              { tag: 'AgroTech', cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300', q: '"What was the treatment we applied to the West Block and did it work?"' },
              { tag: 'AgroTech', cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300', q: '"How much fungicide have we applied this season across all fields?"' },
              { tag: 'Oil & Gas', cls: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300', q: '"Show me any vegetation die-off along Pipeline Sector 7 in the last 30 days"' },
              { tag: 'Oil & Gas', cls: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300', q: '"Which corridors have active anomalies pending HSSE sign-off?"' },
              { tag: 'Infrastructure', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300', q: '"Has there been any unauthorised construction within 500 m of the highway corridor this month?"' },
              { tag: 'Infrastructure', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300', q: '"How much earth has been moved on Site C this month and is construction on schedule?"' },
              { tag: 'Linear Assets', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300', q: '"Which power line segments on Sector 7 have less than 60 days to vegetation contact?"' },
              { tag: 'Linear Assets', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300', q: '"Are there any active railway track obstructions reported in the last 6 hours?"' },
              { tag: 'Environmental', cls: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300', q: '"Detect any forest clearing in the protected zone since January"' },
              { tag: 'Environmental', cls: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300', q: '"Which protected zones have had canopy loss and what enforcement actions are outstanding?"' },
              { tag: 'Cross-Vertical', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300', q: '"How many critical anomalies have we had this season compared to last year across all areas?"' },
              { tag: 'Cross-Vertical', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300', q: '"Compare Plot 4 and Plot 7 — which is performing better this season?"' },
            ].map((q, i) => (
              <div key={i} className="flex gap-2.5 items-start px-3 py-2 bg-white dark:bg-gray-900">
                <span className={`text-xs font-bold uppercase tracking-wide px-1.5 py-0.5 rounded whitespace-nowrap mt-0.5 flex-shrink-0 ${q.cls}`}>
                  {q.tag}
                </span>
                <span className="text-sm italic text-gray-700 dark:text-gray-300">{q.q}</span>
              </div>
            ))}
          </div>

          <SubTitle>Every Response is Multi-Modal</SubTitle>
          <CapabilityList items={[
            'Written narrative (150–300 words): plain-language answer citing specific dates, field names, index values, and confidence scores',
            'Visual element: relevant map highlight, severity heatmap, or trend chart on the Interactive Spatial Visualiser',
            'Suggested follow-up questions: 2–3 contextually appropriate next questions',
            'Action deep-links: "View on Map", "View Anomaly", "Plan Drone Mission", "Export Report"',
            'Voice-to-text input for hands-free field use',
            'Temporal references resolved against site timezone: "last 3 months", "since monsoon", "year-over-year"',
            'Organisational hierarchy references resolved by name including fuzzy matching: "East Block", "Sector 7", "Plot 4A"',
            'Conversation exportable as PDF for audit trail or management reporting',
          ]} />

          {/* ══════════════════════════════════════════════════════
              COMPETITIVE ADVANTAGE
          ══════════════════════════════════════════════════════ */}
          <SectionAnchor id="competitive" />
          <SectionTitle>Competitive Advantage</SectionTitle>

          <DataTable
            headers={['Versus', 'Nabhos Advantage']}
            rows={[
              [<strong>Cropin / Fasal / EOSDA</strong>, 'The only platform that closes the loop from satellite alert → precision drone survey → drone-to-drone remediation spray — with a full conversational AI layer throughout. Competitors stop at the satellite layer.'],
              [<strong>Orbital Insight / SAP iEnergy</strong>, 'Single unified platform for pipelines, farms, roads, and forests using free satellite data — not costly proprietary constellations. One integration instead of four.'],
              [<strong>DroneDeploy / Bentley iTwin</strong>, 'Continuous automated satellite monitoring between drone flights; proactive AI anomaly alerts instead of reactive manual inspection. Satellite screening eliminates unnecessary drone flights.'],
              [<strong>Any single-vertical tool</strong>, 'One licence, one integration, one conversational AI — simultaneously serving agriculture, energy, infrastructure, and environmental. Metadata-driven: industry-specific behaviour controlled by configuration, not code.'],
            ]}
          />

          <SubTitle>Platform Differentiators</SubTitle>
          <CapabilityList items={[
            'Macro-to-Micro Integration: seamless satellite anomaly → drone survey → remediation chain with full audit linkage',
            'Conversational Intelligence: natural language interface that knows your entire operational history across all verticals',
            'Zero satellite compute cost: all index calculations offloaded to Google Earth Engine and Copernicus — free tier',
            'Zero vendor lock-in: Sentinel-2 and Landsat are open, public, and global',
            'Single parameterised codebase: five verticals share the same React components, same API services, same database architecture',
            'Human-in-the-Loop at every gate: AI recommends, humans decide, every decision is auditable',
            'Grounded-only AI: the platform never invents a finding — every assertion is traceable to a specific satellite tile or drone frame',
          ]} />

          {/* ── Closing ──────────────────────────────────────────── */}
          <div className="mt-10 border-t-2 border-gray-200 dark:border-gray-700 pt-6 text-center">
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              One Platform. Five Verticals. One Intelligence Loop.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto mb-4">
              The same 9-step architecture deployed with vertical-specific vocabulary and thresholds. One licence. One integration. One conversational AI. Zero vendor lock-in. Enterprise-grade trust and compliance built in from day one.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {['AgroTech', 'Precision Planning', 'Oil & Gas', 'Infrastructure', 'Environmental Geotech'].map((v) => (
                <span key={v} className="text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-full px-3 py-1 text-gray-700 dark:text-gray-300">
                  {v}
                </span>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// Made with Bob
